/**
 * Identity of the thing this Admin Panel administers, as pure functions.
 *
 * Separated from `ConnectionScope.tsx` (and named distinctly, not `connectionScope.ts`: two modules differing only in case resolve to whichever the bundler reaches first on a case-insensitive filesystem, and rspack picked the `.ts` — so the component's exports silently vanished from the bundle) so it can be tested without a DOM: the component reads the
 * auth store, and vitest runs in a `node` environment here. See §13 of the frontend rules — extract
 * a pure module when the logic needs isolated testing.
 *
 * The concept: an "organization" in this product is one connected Salesforce environment, not the
 * customer's company. See the header of `ConnectionScope.tsx` for why that distinction is worth
 * this much care.
 */

/** Environment names as the login screen offers them. */
const ENVIRONMENT_LABEL: Record<string, string> = {
  production: 'Production',
  sandbox: 'Sandbox',
  developer_org: 'Developer',
  test_sandbox: 'Test sandbox',
};

/**
 * `orgType` is typed as a bare `string` on the auth store, not a union, so an unrecognised value
 * has to render as *something* rather than vanish.
 *
 * A missing chip would be read as "no environment information", which at a glance is
 * indistinguishable from "not production" — and inferring production from an absence is exactly the
 * inference that gets someone administering the wrong environment. So an unknown value is humanised
 * rather than dropped, and only a genuinely empty value returns null.
 */
export function environmentLabel(orgType: string | null | undefined): string | null {
  if (!orgType) return null;
  const known = ENVIRONMENT_LABEL[orgType];
  if (known) return known;
  return orgType
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Exact match only.
 *
 * Deliberately not a substring or prefix test: `test_sandbox` and any future `production_sandbox`
 * style value must NOT read as production, because the whole point of the marker is that the
 * riskiest environment is the one that announces itself.
 */
export function isProductionEnvironment(orgType: string | null | undefined): boolean {
  return orgType === 'production';
}

/**
 * The instance host — the only thing that reliably tells two connections apart.
 *
 * Two Salesforce environments routinely share a display name ("Acme" production and "Acme" UAT), so
 * the host is what disambiguates them. Parsed with `URL` rather than string-trimmed: returns null on
 * anything unparseable so a malformed `instanceUrl` renders nothing, instead of leaking a broken
 * identifier into the page header where it would look like a real hostname.
 */
export function connectionHost(instanceUrl: string | null | undefined): string | null {
  if (!instanceUrl) return null;
  try {
    return new URL(instanceUrl).host || null;
  } catch {
    return null;
  }
}
